import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Scale,
  Building2,
  ShoppingCart,
  Briefcase,
  GitMerge,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  ListChecks,
  History,
  Loader2,
  RefreshCw,
} from "lucide-react";
import {
  useEvaluateBuildBuy,
  useBuildBuyHistory,
  BuildBuyInputs,
  BuildBuyResult,
  BuildBuyRecommendation,
} from "@/hooks/useBuildBuyEvaluation";
import { toast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName?: string;
}

interface Step {
  id: keyof BuildBuyInputs;
  title: string;
  description: string;
  type: "select" | "text";
  options?: { value: string; label: string }[];
  notesField?: keyof BuildBuyInputs;
  placeholder?: string;
  optional?: boolean;
}

const STEPS: Step[] = [
  {
    id: "nature",
    title: "Naturaleza de la necesidad",
    description: "¿Qué tan central es esta capacidad para tu negocio?",
    type: "select",
    notesField: "nature_notes",
    options: [
      { value: "core_differentiator", label: "Core / diferenciador competitivo" },
      { value: "operational", label: "Soporte operacional importante" },
      { value: "commodity", label: "Commodity / genérico del mercado" },
    ],
    placeholder: "Notas adicionales (opcional)",
  },
  {
    id: "data_sensitivity",
    title: "Datos involucrados",
    description: "Sensibilidad, residencia e integración con sistemas internos.",
    type: "select",
    notesField: "data_notes",
    options: [
      { value: "high_pii", label: "Datos altamente sensibles (PII, financieros, salud)" },
      { value: "internal_confidential", label: "Datos internos confidenciales" },
      { value: "low_public", label: "Datos públicos o de baja sensibilidad" },
    ],
    placeholder: "Residencia geográfica, sistemas a integrar, etc.",
  },
  {
    id: "urgency",
    title: "Urgencia y plazo",
    description: "¿En cuánto tiempo necesitas la solución en producción?",
    type: "select",
    options: [
      { value: "urgent", label: "Urgente (< 3 meses)" },
      { value: "moderate", label: "Moderado (3-6 meses)" },
      { value: "flexible", label: "Flexible (6+ meses)" },
    ],
  },
  {
    id: "internal_capacity",
    title: "Capacidad interna",
    description: "Talento disponible, equipo y capacidad de mantenimiento a largo plazo.",
    type: "text",
    placeholder: "Ej: tenemos 2 ingenieros con experiencia en ML pero sin DevOps dedicado…",
  },
  {
    id: "budget",
    title: "Presupuesto disponible",
    description: "Rango y preferencia entre inversión inicial (CAPEX) y costos recurrentes (OPEX).",
    type: "select",
    notesField: "budget_notes",
    options: [
      { value: "capex_high", label: "Alta inversión inicial disponible (CAPEX)" },
      { value: "opex_predictable", label: "Preferimos OPEX predecible" },
      { value: "tight", label: "Presupuesto ajustado / a justificar" },
    ],
    placeholder: "Rango aproximado en USD o moneda local",
  },
  {
    id: "regulatory_notes",
    title: "Restricciones regulatorias específicas",
    description: "Adicionales a las que ya están en el CoE (opcional).",
    type: "text",
    placeholder: "Ej: cumplimiento PCI-DSS, ISO 27001, ley local de IA…",
    optional: true,
  },
  {
    id: "candidate_vendor",
    title: "¿Existe un proveedor o SaaS candidato?",
    description: "Si ya tienes un nombre en mente, indícalo (opcional).",
    type: "text",
    placeholder: "Ej: OpenAI Enterprise, Salesforce Einstein, proveedor X…",
    optional: true,
  },
  {
    id: "free_notes",
    title: "Notas finales para el evaluador",
    description: "Cualquier contexto adicional que la IA deba tener en cuenta (opcional).",
    type: "text",
    placeholder: "Decisiones previas, restricciones políticas internas, etc.",
    optional: true,
  },
];

const RECOMMENDATION_META: Record<
  BuildBuyRecommendation,
  { label: string; icon: typeof Building2; color: string; bg: string }
> = {
  buy_saas: {
    label: "COMPRAR SaaS",
    icon: ShoppingCart,
    color: "text-success",
    bg: "bg-success/15",
  },
  build_internal: {
    label: "CONSTRUIR INTERNO",
    icon: Building2,
    color: "text-primary",
    bg: "bg-primary/15",
  },
  outsource_dev: {
    label: "TERCERIZAR DESARROLLO",
    icon: Briefcase,
    color: "text-warning",
    bg: "bg-warning/15",
  },
  hybrid: {
    label: "ENFOQUE HÍBRIDO",
    icon: GitMerge,
    color: "text-accent-foreground",
    bg: "bg-accent",
  },
};

export function BuildVsBuyDialog({ open, onOpenChange, projectId, projectName }: Props) {
  const [step, setStep] = useState(0);
  const [inputs, setInputs] = useState<BuildBuyInputs>({});
  const [result, setResult] = useState<BuildBuyResult | null>(null);
  const [viewHistory, setViewHistory] = useState(false);

  const evaluate = useEvaluateBuildBuy();
  const history = useBuildBuyHistory(projectId);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setStep(0);
      setInputs({});
      setResult(null);
      setViewHistory(false);
    }
  }, [open]);

  const totalSteps = STEPS.length;
  const progress = ((step + 1) / totalSteps) * 100;
  const current = STEPS[step];
  const currentValue = current ? inputs[current.id] : undefined;
  const currentNotesValue = current?.notesField ? inputs[current.notesField] : undefined;
  const canContinue = current?.optional || (currentValue && String(currentValue).trim().length > 0);

  const handleEvaluate = async () => {
    try {
      const saved = await evaluate.mutateAsync({ projectId, inputs });
      setResult(saved.result);
    } catch (e: any) {
      toast({
        title: "Error al evaluar",
        description: e?.message ?? "Intenta de nuevo.",
        variant: "destructive",
      });
    }
  };

  const lastEvaluation = history.data?.[0];

  const recMeta = useMemo(
    () => (result ? RECOMMENDATION_META[result.recommendation] : null),
    [result],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Scale className="w-5 h-5 text-primary" />
            Build / Buy / Outsource — Evaluación estratégica
          </DialogTitle>
          <DialogDescription>
            {projectName
              ? `Evalúa cómo abordar "${projectName}" cruzando contexto empresarial (CoE, diagnóstico, riesgos, normativa) y el protocolo de tu organización.`
              : "Decide la mejor vía: comprar SaaS, desarrollar internamente o tercerizar."}
          </DialogDescription>
        </DialogHeader>

        {/* Last evaluation banner */}
        {!result && !evaluate.isPending && lastEvaluation && (
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border border-border text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <History className="w-4 h-4" />
              Última evaluación:{" "}
              <span className="font-medium text-foreground">
                {RECOMMENDATION_META[lastEvaluation.result.recommendation]?.label ?? "—"}
              </span>{" "}
              · hace {formatDistanceToNow(new Date(lastEvaluation.created_at), { locale: es })}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setResult(lastEvaluation.result)}>
              Ver
            </Button>
          </div>
        )}

        {/* HISTORY MODE */}
        {viewHistory ? (
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Historial de evaluaciones</h3>
              <Button variant="ghost" size="sm" onClick={() => setViewHistory(false)}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Volver
              </Button>
            </div>
            {history.isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : history.data && history.data.length > 0 ? (
              history.data.map((h) => {
                const meta = RECOMMENDATION_META[h.result.recommendation];
                const Icon = meta?.icon ?? Scale;
                return (
                  <Card key={h.id} className="cursor-pointer hover:border-primary/50" onClick={() => { setResult(h.result); setViewHistory(false); }}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${meta?.bg}`}>
                        <Icon className={`w-5 h-5 ${meta?.color}`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{meta?.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(h.created_at), { locale: es, addSuffix: true })} · confianza {h.result.confidence}%
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <p className="text-center text-muted-foreground py-6">Aún no hay evaluaciones.</p>
            )}
          </div>
        ) : evaluate.isPending ? (
          /* LOADING */
          <div className="py-12 text-center space-y-4">
            <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
            <div>
              <p className="font-semibold">Analizando con IA…</p>
              <p className="text-sm text-muted-foreground mt-1">
                Cruzando tu diagnóstico, CoE, riesgos, normativa y el protocolo de tu empresa.
              </p>
            </div>
          </div>
        ) : result && recMeta ? (
          /* RESULT */
          <div className="space-y-5 py-2">
            <div className="text-center space-y-3">
              <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center ${recMeta.bg}`}>
                <recMeta.icon className={`w-10 h-10 ${recMeta.color}`} />
              </div>
              <Badge className={`text-base px-4 py-1 ${recMeta.bg} ${recMeta.color} border-0`}>
                {recMeta.label}
              </Badge>
              <p className="text-sm text-muted-foreground">
                Confianza: <span className="font-semibold text-foreground">{result.confidence}%</span>
              </p>
            </div>

            <Card>
              <CardContent className="p-4 space-y-2">
                <h4 className="font-semibold flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  Justificación
                </h4>
                <p className="text-sm text-foreground/90">{result.rationale}</p>
              </CardContent>
            </Card>

            <Accordion type="multiple" defaultValue={["alignment", "next_steps"]} className="space-y-2">
              <AccordionItem value="alignment" className="border rounded-lg px-3">
                <AccordionTrigger className="text-sm font-semibold">
                  <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-success" /> Alineación con protocolo y normativa</span>
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">{result.alignment}</AccordionContent>
              </AccordionItem>

              <AccordionItem value="next_steps" className="border rounded-lg px-3">
                <AccordionTrigger className="text-sm font-semibold">
                  <span className="flex items-center gap-2"><ListChecks className="w-4 h-4 text-primary" /> Próximos pasos</span>
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="text-sm space-y-1.5 list-decimal pl-5">
                    {result.next_steps?.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="conditions" className="border rounded-lg px-3">
                <AccordionTrigger className="text-sm font-semibold">
                  <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-warning" /> Condiciones que deben cumplirse</span>
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="text-sm space-y-1.5 list-disc pl-5 text-muted-foreground">
                    {result.conditions?.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="risks" className="border rounded-lg px-3">
                <AccordionTrigger className="text-sm font-semibold">
                  <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-destructive" /> Riesgos clave</span>
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="text-sm space-y-1.5 list-disc pl-5 text-muted-foreground">
                    {result.risks?.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="alternatives" className="border rounded-lg px-3">
                <AccordionTrigger className="text-sm font-semibold">
                  <span className="flex items-center gap-2"><GitMerge className="w-4 h-4" /> Otras opciones consideradas</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-2 text-sm">
                  {result.alternatives && Object.entries(result.alternatives).map(([k, v]) => (
                    <div key={k}>
                      <p className="font-medium text-foreground">
                        {RECOMMENDATION_META[k as BuildBuyRecommendation]?.label ?? k}
                      </p>
                      <p className="text-muted-foreground">{v}</p>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" onClick={() => { setResult(null); setStep(0); setInputs({}); }} className="gap-2">
                <RefreshCw className="w-4 h-4" /> Nueva evaluación
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setViewHistory(true)} className="gap-2">
                  <History className="w-4 h-4" /> Historial
                </Button>
                <Button onClick={() => onOpenChange(false)}>Cerrar</Button>
              </div>
            </div>
          </div>
        ) : (
          /* WIZARD */
          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Paso {step + 1} de {totalSteps}</span>
                <span className="text-primary font-medium">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            <div className="space-y-3">
              <div>
                <h3 className="text-lg font-semibold">{current.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{current.description}</p>
              </div>

              {current.type === "select" && current.options && (
                <Select
                  value={(currentValue as string) ?? ""}
                  onValueChange={(v) => setInputs({ ...inputs, [current.id]: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una opción" />
                  </SelectTrigger>
                  <SelectContent>
                    {current.options.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {current.type === "text" && (
                <Textarea
                  value={(currentValue as string) ?? ""}
                  onChange={(e) => setInputs({ ...inputs, [current.id]: e.target.value })}
                  placeholder={current.placeholder}
                  className="min-h-[100px]"
                />
              )}

              {current.notesField && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Notas adicionales (opcional)</Label>
                  <Textarea
                    value={(currentNotesValue as string) ?? ""}
                    onChange={(e) =>
                      setInputs({ ...inputs, [current.notesField!]: e.target.value })
                    }
                    placeholder={current.placeholder}
                    className="min-h-[60px]"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" /> Anterior
              </Button>
              {step === totalSteps - 1 ? (
                <Button onClick={handleEvaluate} disabled={!canContinue} className="gap-2">
                  <Sparkles className="w-4 h-4" /> Evaluar con IA
                </Button>
              ) : (
                <Button onClick={() => setStep((s) => s + 1)} disabled={!canContinue} className="gap-2">
                  Siguiente <ArrowRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
